// The thank-you page (/thanks/:id), design 8.7: honest status from
// GET /api/consult/:id/status, "Here's what Otto heard" when it exists, the
// one-tap rating, and the callback link from config. The approved Sept 13 look
// (ported from ottos-consult src/ConsultPage.jsx ThankYou, PORTED-FROM.md).
//
// Honest means: the page says only what the status proves. Until the consult is
// packaged it says "saving"; a held consult asks for a few more minutes; after
// 10 minutes with no progress it says it is taking longer and a person will
// reach out. It never claims an email was sent (the webhook is OFF by default,
// Sept 28 2026; StatusResponse.handed_over says only that the webhook accepted
// the package, and nothing reports a sent assessment in this phase).
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type { StatusResponse } from "@otto/api";
import type { z } from "zod";
import { getCheckinConfig, getConsultStatus, postRating } from "./api";
import { ConsultBanner, PublicFooter, PublicHeader } from "./checkin/PublicChrome";
import { readConsultRecord } from "./session";
import { SITE, stringsFor, type ConsultStrings } from "./strings";
import { stripDashes } from "./visibleText";
import "./consult.css";

type Status = z.infer<typeof StatusResponse>;

/** Poll gently: quick at first, then slower; stop once the status can no longer change on its own. */
export const POLL_DELAYS_MS = [2000, 3000, 5000, 8000, 13000, 20000, 30000] as const;
const STILL_MOVING = new Set<Status["status"]>(["checked_in", "live", "ended", "packaging"]);
/** "It's taking longer than usual" after this long without reaching a resting state (design 8.7). */
export const DELAYED_AFTER_MS = 10 * 60 * 1000;

export interface ThanksCopy {
  tone: "done" | "saved" | "wait";
  title: string;
  detail: string;
  action?: "resume" | "back_to_call" | "start_new";
}

/** What the page says for a status. Pure, so every state is tested. */
export function thanksCopy(s: ConsultStrings, status: Status | null, waitedMs: number): ThanksCopy {
  if (!status) return { tone: "wait", title: s.thanks.saving, detail: s.thanks.savingDetail };
  if (status.human_requested && status.status !== "withdrawn")
    return { tone: "saved", title: s.thanks.person, detail: s.thanks.personDetail };
  switch (status.status) {
    case "withdrawn":
      return { tone: "saved", title: s.thanks.withdrawn, detail: s.thanks.withdrawnDetail };
    case "held":
      return {
        tone: "saved",
        title: s.thanks.held,
        // No promise of a resume that cannot happen yet (review fix, Sept 28 2026).
        detail: status.can_resume ? s.thanks.heldDetail : s.thanks.heldDetailNoResume,
        ...(status.can_resume ? { action: "resume" as const } : {}),
      };
    case "failed":
      return { tone: "saved", title: s.thanks.delayed, detail: s.thanks.delayedDetail };
    case "abandoned":
      return { tone: "saved", title: s.thanks.notStarted, detail: s.thanks.notStartedDetail, action: "start_new" };
    case "checked_in":
    case "live":
      return { tone: "wait", title: s.thanks.stillOpen, detail: s.thanks.stillOpenDetail, action: "back_to_call" };
    case "packaged":
      return { tone: "done", title: s.thanks.ready, detail: s.thanks.readyDetail };
    case "ended":
    case "packaging":
    default:
      // The server measures "delayed" from the end of the call (StatusResponse.phase); this page's own wait also counts.
      if (waitedMs >= DELAYED_AFTER_MS || status.phase === "delayed")
        return { tone: "saved", title: s.thanks.delayed, detail: s.thanks.delayedDetail };
      return { tone: "wait", title: s.thanks.saving, detail: s.thanks.savingDetail };
  }
}

function Rating({ s, consultId }: { s: ConsultStrings; consultId: string }) {
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const send = async (chosen: number, text: string) => {
    setState("sending");
    const trimmed = text.trim();
    const result = await postRating(consultId, {
      score: chosen,
      ...(trimmed ? { comment: trimmed.slice(0, 1000) } : {}),
    });
    setState(result.ok ? "sent" : "failed");
  };
  if (state === "sent")
    return (
      <p className="oo-rating__thanks" role="status">
        {s.thanks.ratingThanks}
      </p>
    );
  return (
    <form
      className="oo-rating"
      onSubmit={(event) => {
        event.preventDefault();
        if (score !== null) void send(score, comment);
      }}
    >
      <fieldset>
        <legend>{s.thanks.ratingQuestion}</legend>
        <div className="oo-rating__scores">
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              className={score === value ? "is-selected" : ""}
              aria-pressed={score === value}
              aria-label={s.thanks.ratingLabel(value)}
              onClick={() => {
                // One tap is enough: the score is sent at once; the comment can follow.
                setScore(value);
                void send(value, comment);
              }}
              disabled={state === "sending"}
            >
              {value}
            </button>
          ))}
        </div>
      </fieldset>
      {score !== null && state !== "sending" && (
        <>
          <label className="oo-rating__comment">
            <span>{s.thanks.ratingComment}</span>
            <textarea value={comment} onChange={(event) => setComment(event.target.value)} maxLength={1000} rows={3} />
          </label>
          {comment.trim() && <button type="submit">{s.thanks.ratingSend}</button>}
        </>
      )}
      {state === "failed" && (
        <p className="oo-rating__error" role="alert">
          {s.thanks.ratingFailed}
        </p>
      )}
    </form>
  );
}

export function ThanksPage() {
  const { id = "" } = useParams();
  const record = readConsultRecord(id);
  const language = record?.language ?? "en";
  const s = stringsFor(language);
  const [status, setStatus] = useState<Status | null>(null);
  const [readFailed, setReadFailed] = useState(false);
  const [callbackUrl, setCallbackUrl] = useState<string>(SITE.contact);
  const startedAt = useRef(Date.now());
  const [now, setNow] = useState(() => Date.now());

  const poll = useCallback(
    async (attempt: number, cancelled: () => boolean) => {
      const result = await getConsultStatus(id);
      if (cancelled()) return;
      setNow(Date.now());
      if (result.ok) {
        setStatus(result.data);
        setReadFailed(false);
        if (!STILL_MOVING.has(result.data.status)) return;
      } else {
        setReadFailed(true);
      }
      const delay = POLL_DELAYS_MS[Math.min(attempt, POLL_DELAYS_MS.length - 1)] ?? 30_000;
      setTimeout(() => {
        if (!cancelled() && document.visibilityState !== "hidden") void poll(attempt + 1, cancelled);
        else if (!cancelled()) void poll(attempt, cancelled);
      }, delay);
    },
    [id],
  );

  useEffect(() => {
    let stop = false;
    void poll(0, () => stop);
    void getCheckinConfig().then((result) => {
      if (!stop && result.ok && result.data.callback_url) setCallbackUrl(result.data.callback_url);
    });
    return () => {
      stop = true;
    };
  }, [poll]);

  const copy = thanksCopy(s, status, now - startedAt.current);
  const firstName = record?.first_name ?? "";
  const business = (record?.business_name ?? "").replace(/[\s.!?]+$/, "");
  const heard = status?.what_otto_heard ? stripDashes(status.what_otto_heard) : null;
  const finished = copy.tone === "done" || (status !== null && !STILL_MOVING.has(status.status));

  return (
    <div className="consult-root oo-consult-root oo-consult-root--complete" lang={language}>
      <PublicHeader s={s} />
      <ConsultBanner label={s.banner.free}>{copy.tone === "done" ? s.banner.done : s.banner.saved}</ConsultBanner>
      <main className="oo-thank-you">
        <section className="oo-thank-you__hero">
          <div className="oo-thank-you__layout">
            <figure className="oo-thank-you__photo">
              <img src="/images/consult/otto-consult-call.jpg" alt={s.thanks.photoAlt} />
              <figcaption>
                <span aria-hidden="true" />
                {copy.tone === "done" ? s.thanks.complete : s.thanks.saved}
              </figcaption>
            </figure>
            <div className="oo-thank-you__body">
              <img className="oo-thank-you__burst" src="/images/consult/starburst-yellow.png" alt="" />
              <h1>
                {firstName ? s.thanks.thankYou(firstName) : s.thanks.thankYouNoName}{" "}
                {copy.tone !== "saved" && <span>{s.thanks.allSet}</span>}
              </h1>
              {business && <p className="oo-thank-you__copy">{s.thanks.toldOtto(business)}</p>}

              <div className={`oo-thank-you__notice is-${copy.tone}`} role="status" aria-live="polite">
                <img src="/images/consult/starburst-mustard.png" alt="" />
                <p>
                  <strong>{copy.title}</strong>
                  {copy.detail}
                  {readFailed && <em className="oo-thank-you__soft"> {s.thanks.cannotRead}</em>}
                </p>
              </div>

              {language === "es" && s.thanks.englishReport && (
                <p className="oo-thank-you__next">{s.thanks.englishReport}</p>
              )}

              {heard && (
                <section className="oo-heard" aria-labelledby="oo-heard-title">
                  <h2 id="oo-heard-title">{s.thanks.whatOttoHeard}</h2>
                  <p>{heard}</p>
                </section>
              )}

              {copy.action === "resume" && (
                <a className="oo-thank-you__button" href={`/call/${id}`}>
                  {s.thanks.resume}
                </a>
              )}
              {copy.action === "back_to_call" && (
                <a className="oo-thank-you__button" href={`/call/${id}`}>
                  {s.thanks.backToCall}
                </a>
              )}

              {finished && status?.status !== "withdrawn" && status?.status !== "abandoned" && (
                <Rating s={s} consultId={id} />
              )}

              <div className="oo-thank-you__actions">
                <a className="is-primary" href={SITE.home}>
                  {s.thanks.visitSite}
                </a>
                <a href={callbackUrl}>{s.thanks.callback}</a>
              </div>
              {copy.action === "start_new" && (
                <a className="oo-thank-you__restart" href="/">
                  {s.thanks.startAnother}
                </a>
              )}
            </div>
          </div>
        </section>
      </main>
      <PublicFooter s={s} />
    </div>
  );
}
